// C:\Ava_Local\Ava_API\avaai_dashboard\frontend\src\pages\Education.tsx
import React, { useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";

type EducationItem = {
  title: string;
  subtitle: string;
  path: string;
  icon: string;
  glow: string;
  featured?: boolean;
};

function makeEducationStyles(themeColors: any) {
  const pageStyle: React.CSSProperties = {
    minHeight: "100vh",
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 8px 10px",
    background: themeColors.bg,
    color: themeColors.text,
    overflowX: "hidden",
  };

  const shellStyle: React.CSSProperties = {
    maxWidth: "1600px",
    margin: "0 auto",
  };

  const panelStyle: React.CSSProperties = {
    borderRadius: "18px",
    padding: "9px 12px",
    background: themeColors.panel,
    border: `1px solid ${themeColors.border}`,
    boxShadow: themeColors.glow,
    backdropFilter: "blur(8px)",
  };

  const cardBase: React.CSSProperties = {
    appearance: "none",
    width: "100%",
    minHeight: "90px",
    borderRadius: "18px",
    padding: "11px 12px",
    textAlign: "left",
    cursor: "pointer",
    transition:
      "transform 0.14s ease, box-shadow 0.14s ease, border-color 0.14s ease, background 0.14s ease",
    background: themeColors.cardBg,
    color: themeColors.text,
    border: `1px solid ${themeColors.border}`,
    boxShadow:
      "0 4px 12px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.02)",
  };

  return { pageStyle, shellStyle, panelStyle, cardBase };
}

export default function Education() {
  const navigate = useNavigate();
  const { themeColors } = useContext(ThemeContext);
  const {
    pageStyle,
    shellStyle,
    panelStyle,
    cardBase,
  } = useMemo(() => makeEducationStyles(themeColors), [themeColors]);

  const featuredItems: EducationItem[] = [
    {
      title: "Math Classroom",
      subtitle: "Ask Ava broader math questions and learn at your grade level.",
      path: "/education/math",
      icon: "∑",
      glow: "rgba(0, 224, 255, 0.35)",
      featured: true,
    },
    {
      title: "History",
      subtitle: "Explore people, events, timelines, government, and civics.",
      path: "/education/history",
      icon: "H",
      glow: "rgba(255, 190, 100, 0.30)",
      featured: true,
    },
    {
      title: "Reading",
      subtitle: "Practice comprehension, vocabulary, passages, and literary analysis.",
      path: "/education/reading",
      icon: "R",
      glow: "rgba(150, 120, 255, 0.32)",
      featured: true,
    },
    {
      title: "Science",
      subtitle: "Learn biology, chemistry, physics, Earth, and space science.",
      path: "/education/science",
      icon: "S",
      glow: "rgba(80, 255, 170, 0.30)",
      featured: true,
    },
    {
      title: "Math Board",
      subtitle: "Type a problem and let Ava solve it step by step.",
      path: "/education/mathboard",
      icon: "🧮",
      glow: "rgba(0, 255, 170, 0.28)",
      featured: true,
    },
    {
      title: "Ava Tutor",
      subtitle: "Ask Ava questions, get help, examples, and encouragement.",
      path: "/education/tutor",
      icon: "✦",
      glow: "rgba(90, 220, 255, 0.35)",
      featured: true,
    },
  ];

    const primaryItems: EducationItem[] = [
      {
        title: "Addition",
        subtitle: "Practice adding numbers with Ava step by step.",
        path: "/education/addition",
        icon: "+",
        glow: "rgba(0, 224, 255, 0.35)",
      },
      {
        title: "Subtraction",
        subtitle: "Learn how to subtract with hints and examples.",
        path: "/education/subtraction",
        icon: "−",
        glow: "rgba(120, 92, 255, 0.34)",
      },
      {
        title: "Multiplication",
        subtitle: "Build multiplication skills with guided practice.",
        path: "/education/multiplication",
        icon: "×",
        glow: "rgba(0, 255, 204, 0.30)",
      },
      {
        title: "Division",
        subtitle: "Break division down into easier steps and groups.",
        path: "/education/division",
        icon: "÷",
        glow: "rgba(255, 170, 0, 0.26)",
      },
      {
        title: "Fractions",
        subtitle: "Equivalent fractions and fraction math.",
        path: "/education/fractions",
        icon: "½",
        glow: "rgba(0, 255, 170, 0.28)",
      },
      {
        title: "Functions",
        subtitle: "Rules, inputs, outputs, and expressions.",
        path: "/education/functions",
        icon: "ƒ",
        glow: "rgba(255, 120, 220, 0.26)",
      },
      {
        title: "Decimals",
        subtitle: "Learn decimal math, comparisons, rounding, and place value.",
        path: "/education/decimals",
        icon: "0.1",
        glow: "rgba(0, 255, 255, 0.28)",
      },
      {
        title: "Essay Writing",
        subtitle: "Grammar help, introductions, conclusions, and essay guidance.",
        path: "/education/essay-writing",
        icon: "✍",
        glow: "rgba(255, 180, 80, 0.26)",
      },
    ];

const supportItems: EducationItem[] = [
    {
      title: "Flash Cards",
      subtitle: "Quick practice cards for core math skills.",
      path: "/education/flashcards",
      icon: "▣",
      glow: "rgba(255, 80, 170, 0.28)",
    },
    {
      title: "Grade Levels",
      subtitle: "Choose the right learning level.",
      path: "/education/grades",
      icon: "A",
      glow: "rgba(90, 255, 140, 0.26)",
    },
    {
      title: "Back to Games",
      subtitle: "Return to the Games Hub.",
      path: "/games",
      icon: "←",
      glow: "rgba(255, 210, 110, 0.26)",
    },
  ];

  function applyHover(
    e: React.MouseEvent<HTMLButtonElement>,
    glow: string,
    featured?: boolean
  ) {
    e.currentTarget.style.transform = "translateY(-1px)";
    e.currentTarget.style.boxShadow = `0 8px 18px rgba(0,0,0,0.24), 0 0 12px ${glow}`;
    e.currentTarget.style.borderColor = themeColors.accent;
    if (featured) {
      e.currentTarget.style.background = themeColors.cardBg;
    }
  }

  function clearHover(
    e: React.MouseEvent<HTMLButtonElement>,
    featured?: boolean
  ) {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow =
      "0 4px 12px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.02)";
    e.currentTarget.style.borderColor = themeColors.border;
    e.currentTarget.style.background = themeColors.cardBg;
  }

  function renderCard(item: EducationItem) {
    return (
      <button
        key={item.title}
        type="button"
        onClick={() => navigate(item.path)}
        style={{
          ...cardBase,
          minHeight: item.featured ? "94px" : "90px",
          border: item.featured
            ? `1px solid ${themeColors.accent}`
            : cardBase.border,
          background: themeColors.cardBg,
        }}
        onMouseEnter={(e) => applyHover(e, item.glow, item.featured)}
        onMouseLeave={(e) => clearHover(e, item.featured)}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "40px minmax(0, 1fr)",
            gap: "10px",
            alignItems: "start",
          }}
        >
          <div
            style={{
              width: item.featured ? "40px" : "38px",
              height: item.featured ? "40px" : "38px",
              borderRadius: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize:
                item.icon === "🧮"
                  ? "18px"
                  : item.icon === "½" || item.icon === "ƒ"
                  ? "20px"
                  : "20px",
              fontWeight: 900,
              color: themeColors.text,
              background: `linear-gradient(180deg, ${themeColors.accent}38 0%, ${themeColors.cardBg} 100%)`,
              boxShadow: `0 0 10px ${item.glow}`,
              flexShrink: 0,
            }}
          >
            {item.icon}
          </div>

          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginBottom: "4px",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontSize: item.featured ? "15px" : "14px",
                  fontWeight: 800,
                  letterSpacing: "0.01em",
                  lineHeight: 1.08,
                }}
              >
                {item.title}
              </div>

              {item.featured && (
                <span
                  style={{
                    fontSize: "8px",
                    fontWeight: 900,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    padding: "3px 6px",
                    borderRadius: "18px",
                    color: themeColors.bg,
                    background: `linear-gradient(180deg, ${themeColors.text} 0%, ${themeColors.accent2} 100%)`,
                  }}
                >
                  Featured
                </span>
              )}
            </div>

            <div
              style={{
                fontSize: item.featured ? "11px" : "10.5px",
                lineHeight: 1.24,
                color: themeColors.mutedText,
              }}
            >
              {item.subtitle}
            </div>
          </div>
        </div>
      </button>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={shellStyle}>
        <div style={{ ...panelStyle, marginBottom: "8px" }}>
          <div
            style={{
              fontSize: "10px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: themeColors.accent2,
              fontWeight: 800,
              marginBottom: "2px",
            }}
          >
            AVAAIO Education Center
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "clamp(20px, 2.8vw, 34px)",
              lineHeight: 1,
              fontWeight: 900,
              color: themeColors.accent,
              background: `linear-gradient(90deg, ${themeColors.accent2}, ${themeColors.text})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: themeColors.glow,
              wordBreak: "break-word",
            }}
          >
            Learn With Ava
          </h1>

          <p
            style={{
              margin: "4px 0 0",
              fontSize: "clamp(10px, 1vw, 13px)",
              color: themeColors.mutedText,
              maxWidth: "1100px",
              lineHeight: 1.25,
            }}
          >
            Build skills with Ava through step-by-step math help, guided tutoring,
            flash-card practice, grade-level learning, and interactive tools designed
            to make studying feel easier, faster, and more personal.
          </p>
        </div>

        <div
          style={{
            ...panelStyle,
            marginBottom: "16px",
            padding: "16px 18px",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: 800,
              color: themeColors.text,
              marginBottom: "1px",
            }}
          >
            Featured Learning Tools
          </div>
          <div
            style={{
              fontSize: "11px",
              color: themeColors.mutedText,
              lineHeight: 1.2,
            }}
          >
            Jump into Ava’s strongest learning experiences for solving, tutoring, and guided practice.
          </div>
        </div>

        <div
          className="education-grid education-grid-featured"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "14px",
            alignItems: "stretch",
            marginBottom: "16px",
          }}
        >
          {featuredItems.map(renderCard)}
        </div>

        <div
          style={{
            ...panelStyle,
            marginBottom: "16px",
            padding: "16px 18px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 800,
                  color: themeColors.text,
                  marginBottom: "1px",
                }}
              >
                Core Math Subjects
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: themeColors.mutedText,
                  lineHeight: 1.45,
                }}
              >
                Choose a subject and start practicing with guided explanations, examples, and support.
              </div>
            </div>

            <div
              style={{
                fontSize: "10px",
                color: themeColors.accent2,
                border: `1px solid ${themeColors.border}`,
                borderRadius: "18px",
                padding: "5px 8px",
                background: themeColors.buttonBg,
                lineHeight: 1.1,
              }}
            >
              Math & Writing Paths: addition • subtraction • multiplication • division • fractions • functions • decimals • essay writing
            </div>
          </div>
        </div>

        <div
          className="education-grid education-grid-primary"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "14px",
            alignItems: "stretch",
            marginBottom: "16px",
          }}
        >
          {primaryItems.map(renderCard)}
        </div>

        <div
          style={{
            ...panelStyle,
            marginBottom: "16px",
            padding: "16px 18px",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: 800,
              color: themeColors.text,
              marginBottom: "1px",
            }}
          >
            More Ways to Learn
          </div>
          <div
            style={{
              fontSize: "11px",
              color: themeColors.mutedText,
              lineHeight: 1.2,
            }}
          >
            Extra practice tools, grade-level support, and quick navigation for learning sessions.
          </div>
        </div>

        <div
          className="education-grid education-grid-secondary"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: "14px",
            alignItems: "stretch",
          }}
        >
          {supportItems.map(renderCard)}
        </div>

        <style>{`
          @media (max-width: 1200px) {
            .education-grid-featured,
            .education-grid-primary,
            .education-grid-secondary {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 760px) {
            .education-grid-featured,
            .education-grid-primary,
            .education-grid-secondary {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </div>
    

          <style>{`
            @media (max-width: 768px) {
              button {
                min-height: 48px !important;
              }
            }

            @supports (padding: max(0px)) {
              body {
                padding-bottom: env(safe-area-inset-bottom);
              }
            }
          `}</style>
</div>
  );
}
